import { Box, Button, SortDirection, Tooltip } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { ObjectType, Space } from '@fullstory/proto/fs/services/dx-objects';
import { ALL_LIBRARY_OBJECT_FILTERS, LibraryObjectType } from '@fullstory/routes';
import { classNames } from '@fullstory/util/fmt';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AddFromLibraryModal,
  LIBRARY_TABLE_LAST_MODIFIED_COLUMN_KEY,
  LibraryObject,
  LibraryTable,
  LibraryTableNoResults,
  PaginationControls,
} from '../../components';
import {
  LibraryObjectSearchParams,
  useLibraryObjectSearch,
  useLibraryObjectTypesAccess,
  usePermissions,
  useSpaces,
} from '../../hooks';
import css from './index.module.scss';
import { FormattedMessage } from 'react-intl';
import { WELCOME_DASHBOARD_ID } from '../../utils';

const PAGE_SIZE = 10;
// keep the modal's height consistent by providing the max case for a full page of results
// yes this is brittle, but its the easiest for now
const TABLE_AND_PAGINATION_HEIGHT = 492;
const SPACING = 24;
const SEARCH_DEBOUNCE_MS = 250;

type Params = {
  filter: React.ComponentProps<typeof AddFromLibraryModal>['filter'];
  space: Space | null;
  objectType: LibraryObjectType | null;
  search: string;
  sort: {
    sortColumnKey: string;
    sortDirection: SortDirection;
  };
  page: number;
};

const makeInitialParams = (enabledObjectTypes: LibraryObjectType[]): Params => ({
  filter: 'active',
  space: null,
  // if only one object type is enabled, use it
  objectType: enabledObjectTypes.length === 1 ? enabledObjectTypes[0]! : null,
  search: '',
  sort: {
    sortColumnKey: LIBRARY_TABLE_LAST_MODIFIED_COLUMN_KEY,
    sortDirection: 'desc',
  },
  page: 1,
});

type Action = {
  /**
   * Optionally apply different button text content when the action is disabled.
   *
   * @default "Add"
   */
  buttonText?: React.ComponentProps<typeof Button>['children'];
  /**
   * The button variant to apply.
   *
   * @default fill-primary
   */
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
  /**
   * The tooltip content to wrap the Button with.
   */
  tooltipContent?: React.ComponentProps<typeof Tooltip>['tooltipContent'];
  /**
   * Whether the action button should be disabled.
   *
   * @default false
   */
  disabled?: React.ComponentProps<typeof Button>['disabled'];
  /**
   * Action button click callback.
   *
   * Uses the Promise state to block multiple actions on the same LibraryObject at once.
   */
  onClick: (object: LibraryObject) => Promise<void>;
};

interface AddFromLibraryModalContainerProps {
  open: boolean;
  /**
   * Title of the Modal
   *
   * @default Add from library
   */
  title?: React.ComponentProps<typeof AddFromLibraryModal>['title'];
  /**
   * The current Space context. Space filtering options will exclude the current Space, when provided.
   */
  currentSpaceId?: string;
  /**
   * When provided, the limited set of object types to include in search.  *This must be a stable constant array*.
   *
   * A value of undefined means All enabled object types on the org.
   *
   * Any object type's the org does not have enabled will be excluded internally.
   */
  objectTypes?: LibraryObjectType[];
  /**
   * Do not allow adding the canned dashboard.
   */
  disabledCannedDashboard?: boolean;
  /**
   * An action builder for each LibraryObject row in the table search results.
   */
  includePrivateObjects: boolean;
  /**
   * Override the default reason tooltip content on why private objects are not allowed to be used.
   */
  privateObjectsDisabledReason?: string;
  getObjectAction: (object: LibraryObject) => Action | null;
  /**
   * Optionally provide the width of the actions column in the table. This is useful to
   * tighten up the width of this column, and work with different action button text content lengths.
   *
   * @default undefined
   */
  getActionColumnWidth?: (
    objects: LibraryObject[],
  ) => React.ComponentProps<typeof LibraryTable>['actionsColumnWidth'];
  /**
   * Getter function to collect props of LibraryObject's to create their tooltips.
   */
  getLibraryObjectTooltipProps: React.ComponentProps<
    typeof LibraryTable
  >['getLibraryObjectTooltipProps'];
  onClose: () => void;
}

export const AddFromLibraryModalContainer: React.FC<AddFromLibraryModalContainerProps> = ({
  open,
  currentSpaceId,
  title,
  objectTypes,
  disabledCannedDashboard,
  getObjectAction,
  getActionColumnWidth = () => undefined,
  getLibraryObjectTooltipProps,
  onClose,
  includePrivateObjects,
  privateObjectsDisabledReason,
}) => {
  const { spaces } = useSpaces(open);
  const { canModifySpacesObjects } = usePermissions();
  const filteredSpaces = useMemo(
    () => (currentSpaceId ? spaces.filter(s => s.id !== currentSpaceId) : spaces),
    [spaces, currentSpaceId],
  );
  const enabledObjectTypesAccessMap = useLibraryObjectTypesAccess();
  const enabledObjectTypes = useMemo(() => {
    // if provided, use them, else start with all
    const resolvedObjectTypes = objectTypes ?? ALL_LIBRARY_OBJECT_FILTERS;
    // filter to those with access
    const enabled = resolvedObjectTypes.filter(
      type =>
        enabledObjectTypesAccessMap[type].included &&
        enabledObjectTypesAccessMap[type].supportsLibrary,
    );

    // the way the props are set up with the access check internal, we should fail loudly on this case.
    if (!enabled.length) {
      throw new Error(`The org is not enabled for ObjectTypes ${objectTypes?.join(', ')}`);
    }

    return enabled;
  }, [enabledObjectTypesAccessMap, objectTypes]);

  const [inFlightActionIds, setInFlightActionIds] = useState<string[]>([]);
  useEffect(() => {
    if (!open) setInFlightActionIds(prev => (prev.length ? [] : prev));
  }, [open]);

  const [params, setParams] = useState<Params>(makeInitialParams(enabledObjectTypes));

  useEffect(() => {
    setParams(prev =>
      !prev.objectType || enabledObjectTypes.includes(prev.objectType)
        ? // if the current object type is still enabled, do nothing
          prev
        : // reset if the current objectType is no longer enabled
          { ...prev, objectType: null },
    );
  }, [enabledObjectTypes]);

  // need an intermediate representation for the search value,
  // updates to params need to be debounced to avoid issuing queries on each keystroke
  const [search, setSearch] = useState<string | null | undefined>();
  const debouncedOnSearchChange = useMemo(
    () =>
      debounce(
        (search: string) => setParams(prev => ({ ...prev, search, page: 1 })),
        SEARCH_DEBOUNCE_MS,
      ),
    [],
  );

  const handleSearchChange = (search: string) => {
    setSearch(search);
    debouncedOnSearchChange(search);
  };

  const handleClearFilters = () => {
    setParams(prev => ({
      ...makeInitialParams(enabledObjectTypes),
      // dont reset sort, only the filtering, search, and page params
      sort: prev.sort,
    }));
    setSearch('');
  };

  const libraryObjectSearchParams = useMemo(
    (): LibraryObjectSearchParams => ({
      spaceId: params.space?.id ?? null,
      filter: params.filter,
      search: params.search,
      objectTypes: params.objectType ? [params.objectType] : enabledObjectTypes,
      sort: params.sort,
      page: params.page,
    }),
    [params, enabledObjectTypes],
  );

  const { objects, page, matchingRecords, totalRecords, error, loading, refetch } =
    useLibraryObjectSearch(libraryObjectSearchParams, PAGE_SIZE, true, open);

  const hasResults = !!objects?.length;
  // fades the results and disables action buttons
  const refetching = hasResults && loading;

  return (
    <AddFromLibraryModal
      data-component="AddFromLibraryModalContainer"
      open={open}
      title={title}
      size="large"
      filter={params.filter}
      onFilterChange={filter => setParams(prev => ({ ...prev, filter, page: 1 }))}
      spaces={filteredSpaces}
      selectedSpace={params.space}
      onSpaceChange={space => {
        setParams(prev => ({ ...prev, space, page: 1 }));
      }}
      enabledObjectTypes={enabledObjectTypes}
      selectedObjectType={params.objectType}
      onObjectTypeChange={objectType => setParams(prev => ({ ...prev, objectType, page: 1 }))}
      search={search}
      onSearchChange={handleSearchChange}
      onCloseSignal={onClose}
    >
      <Box
        marginBottom={SPACING}
        className={classNames(css.opacityTransition, {
          [css.fade]: refetching,
        })}
        height={TABLE_AND_PAGINATION_HEIGHT}
        maxWidth={1}
      >
        {hasResults ? (
          <LibraryTable
            refetching={refetching}
            objects={objects!}
            currentSpaceId={undefined}
            showInsights={false}
            showSpaceLinks={false}
            sort={params.sort}
            onSortChange={sort => setParams(prev => ({ ...prev, sort, page: 1 }))}
            actionsColumnWidth={getActionColumnWidth(objects!)}
            renderActions={object => {
              const action = getObjectAction(object);
              const handleActionOnClick = async () => {
                if (inFlightActionIds.includes(object.id) || refetching) {
                  // no-op - block multiple in-flight actions on the same object
                  return;
                }

                try {
                  setInFlightActionIds(prev => prev.concat(object.id));
                  await action?.onClick(object);
                } finally {
                  setInFlightActionIds(prev => prev.filter(id => id !== object.id));
                }
              };

              if (!action) {
                // this keeps height consistent with rows that have a button
                return <Box minHeight={24} />;
              }

              let button = (
                <Button
                  onClick={handleActionOnClick}
                  size="x-small"
                  variant={action.buttonVariant ?? 'fill-primary'}
                  // if we want to disable the button while the action is in-flight:  || inFlightActionIds.includes(object.id)
                  disabled={
                    action.disabled ||
                    (!includePrivateObjects && object.visibility === Visibility.PRIVATE) ||
                    (disabledCannedDashboard &&
                      object.objectType == ObjectType.DASHBOARD &&
                      object.id == WELCOME_DASHBOARD_ID)
                  }
                  style={refetching ? { cursor: 'default' } : undefined}
                  testId="add-card-button"
                >
                  {action.buttonText ??
                    intl.formatMessage({ defaultMessage: 'Add ', id: 'w/y0mv' })}
                </Button>
              );

              if (action.tooltipContent) {
                button = (
                  <Tooltip tooltipContent={action.tooltipContent} asChild>
                    {/* need inline-block to keep the div wrapped around the button only and Not
                    stretching to the full length of the flex item */}
                    <div style={{ display: 'inline-block' }}>{button}</div>
                  </Tooltip>
                );
              } else if (!includePrivateObjects && object.visibility === Visibility.PRIVATE) {
                button = (
                  <Tooltip
                    tooltipContent={
                      privateObjectsDisabledReason ?? (
                        <FormattedMessage
                          defaultMessage="Private objects cannot be added to a public dashboard or space."
                          id="DIfebd"
                        />
                      )
                    }
                    asChild
                  >
                    {/* need inline-block to keep the div wrapped around the button only and Not
                    stretching to the full length of the flex item */}
                    <div style={{ display: 'inline-block' }}>{button}</div>
                  </Tooltip>
                );
              }

              return button;
            }}
            getLibraryObjectTooltipProps={getLibraryObjectTooltipProps}
          />
        ) : (
          <Box padding={[64, SPACING]} width={1}>
            <LibraryTableNoResults
              canModifySpacesObjects={canModifySpacesObjects}
              error={error}
              loading={loading}
              objects={objects}
              totalRecords={totalRecords}
              space={
                params.space?.id
                  ? {
                      zeroStateDescription: intl.formatMessage({
                        defaultMessage: 'Try selecting a different space above.',
                        id: 'fN+4D6',
                      }),
                      noResultsDescription: intl.formatMessage({
                        defaultMessage: 'Nothing in this space matches that search.',
                        id: 'g79/10',
                      }),
                    }
                  : undefined
              }
              onClearFiltersClick={handleClearFilters}
              onRetryClick={refetch}
              startOver={{ onClick: handleClearFilters }}
            />
          </Box>
        )}
        {hasResults && !!page && !!matchingRecords && matchingRecords > PAGE_SIZE && (
          <Box marginTop={SPACING} width="100%">
            <PaginationControls
              page={params.page}
              disablePrev={params.page <= 1}
              disableNext={params.page >= Math.ceil(matchingRecords / PAGE_SIZE)}
              start={(params.page - 1) * PAGE_SIZE + 1}
              end={Math.min(params.page * PAGE_SIZE, matchingRecords)}
              total={matchingRecords}
              loading={false}
              onPrevPageClick={() => setParams(prev => ({ ...prev, page: prev.page - 1 }))}
              onNextPageClick={() => setParams(prev => ({ ...prev, page: prev.page + 1 }))}
            />
          </Box>
        )}
      </Box>
    </AddFromLibraryModal>
  );
};
